/**
 * Builds short, human-readable summary strings for workflow nodes so that
 * the canvas card can read like a sentence (e.g. "Find the first charted account
 * where name contains \"Cash\", sorted by name") instead of just showing a kind label.
 */
import { RuleDefinitionSuggestionTypeEnum } from "@/__generated__/graphql";
import type {
  FilterDefinitionType,
  ReferenceValueOptionsType,
} from "@/components/@types";
import type {
  ActionComponent,
  ComponentFilter,
  ComponentValue,
  EventTriggerConfig,
  LookupComponent,
  StepComponent,
  ThreadComponent,
  TriggerConfig,
} from "../types/rule";
import { scheduleSummaryOrFallback } from "./scheduleDescription";
import { resolveScheduleSpecForSummary } from "./triggerSchedule";

export type SummaryTone =
  | "object"
  | "filter"
  | "sort"
  | "limit"
  | "value"
  | "variable"
  | "expression"
  | "prompt"
  | "schedule"
  | "operation";

export type SummaryPart = {
  text: string;
  tone?: SummaryTone;
  /** Start a new row before this segment (tabular node summary). */
  breakBefore?: boolean;
  /** Indentation for the row this segment starts, in `em`. */
  indent?: number;
  /**
   * When set, ``SummaryText`` resolves this segment at render time by
   * running ``referenceLookup``'s GraphQL query through Apollo's cache
   * and rendering the row that matches ``referenceIdentifier`` via the
   * caller's ``onRenderOption``. Used to turn raw reference UUIDs in
   * lookup filters into the human label the picker shows
   * (e.g. ``"Bill (760.00 CAD) — 49th Parallel Roasters"``). The
   * static ``text`` is used as a fallback while the cache is cold.
   */
  referenceLookup?: ReferenceValueOptionsType<unknown>;
  referenceIdentifier?: string;
  /** Server-provided reference target typename (e.g. ``ContactObject``). */
  referenceObjectType?: string;
};

function text(
  s: string,
  tone?: SummaryTone,
  layout?: { breakBefore?: boolean; indent?: number },
): SummaryPart {
  return {
    text: s,
    tone,
    breakBefore: layout?.breakBefore,
    indent: layout?.indent,
  };
}

/** Inline join: multiple condition groups on one row (e.g. `a and b`, `a, b, or c`). */
function joinParts(groups: SummaryPart[][], joiner: string): SummaryPart[] {
  if (groups.length === 0) return [];
  if (groups.length === 1) return groups[0];
  if (groups.length === 2) {
    return [...groups[0], text(` ${joiner} `, "operation"), ...groups[1]];
  }
  return groups.flatMap((group, index) => {
    if (index === 0) return group;
    const sep = index === groups.length - 1 ? `, ${joiner} ` : ", ";
    return [text(sep), ...group];
  });
}

/** First group stays on the opening row; each further group starts a new indented row with `and` / `or`. */
function joinPartsContinuationRows(
  groups: SummaryPart[][],
  joiner: string,
): SummaryPart[] {
  if (groups.length === 0) return [];
  if (groups.length === 1) return groups[0];
  return [
    ...groups[0],
    ...groups
      .slice(1)
      .flatMap((group) => [
        text(`${joiner} `, "operation", { breakBefore: true, indent: 1 }),
        ...group,
      ]),
  ];
}

function summaryText(parts: SummaryPart[]): string {
  return parts.map((p) => p.text).join("");
}

const OBJECT_TYPE_HUMAN: Record<string, string> = {
  Transaction: "transaction",
  Contact: "contact",
  Document: "document",
  ChartedAccount: "charted account",
  CategoryGroup: "category group",
  Category: "category",
  TagGroup: "tag group",
  Tag: "tag",
  Tax: "tax",
  Entity: "entity",
  JournalEntry: "journal entry",
  FinancialAccount: "financial account",
  Notification: "notification",
  Thread: "thread",
};

const WORKFLOW_METATYPE_WORDS = new Set(["trigger", "switch"]);

/**
 * GraphQL IObject typenames are stored as e.g. `DocumentObject` — map to the same
 * short labels as the legacy unqualified names. Rejects workflow metatypes so a bad
 * `TRIGGER` value is never read as "when a trigger …".
 */
export function humanizeObjectType(objectType?: string | null): string {
  if (!objectType) return "object";
  if (OBJECT_TYPE_HUMAN[objectType]) return OBJECT_TYPE_HUMAN[objectType];
  const withoutObject = objectType.replace(/Object$/, "");
  if (withoutObject !== objectType && OBJECT_TYPE_HUMAN[withoutObject]) {
    return OBJECT_TYPE_HUMAN[withoutObject];
  }
  const s = objectType
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase();
  if (WORKFLOW_METATYPE_WORDS.has(s)) return "object";
  return s;
}

const OPERATOR_PHRASE: Record<string, string> = {
  // generic / English
  equals: "equals",
  not_equals: "does not equal",
  greater_than: "is greater than",
  less_than: "is less than",
  greater_than_or_equal: "is at least",
  less_than_or_equal: "is at most",
  contains: "contains",
  not_contains: "does not contain",
  starts_with: "starts with",
  ends_with: "ends with",
  is_null: "is null",
  is_not_null: "is not null",
  // django-style aliases that may show up on filters
  exact: "equals",
  iexact: "equals",
  icontains: "contains",
  istartswith: "starts with",
  iendswith: "ends with",
  gt: "is greater than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  in: "is one of",
  isnull: "is null",
};

function getConstantBoolean(value: ComponentValue | undefined): boolean | null {
  if (
    !value ||
    !("type" in value) ||
    (value as { type?: string }).type !== "constant"
  ) {
    return null;
  }
  const v = (value as { value: unknown }).value;
  if (v === true || v === "true" || v === 1) return true;
  if (v === false || v === "false" || v === 0) return false;
  return null;
}

/**
 * For null / presence checks, returns "null" or "notNull" so we render
 * "name is null" instead of "Name isNull true".
 *
 * - `is_null` (rules) and GraphQL `isNull` + constant true both compact to
 *   `isnull` — we distinguish by snake_case `is_*` on the original operator.
 * - Django's `isnull` uses constant false to mean "is not null".
 */
function nullCheckOutcome(
  operator: string | undefined | null,
  value: ComponentValue | undefined,
): "null" | "notNull" | null {
  if (!operator) return null;
  const compact = operator.replace(/_/g, "").toLowerCase();
  if (compact === "isnotnull") return "notNull";
  if (compact === "isnull") {
    if (operator.toLowerCase() === "is_null") return "null";
    if (getConstantBoolean(value) === false) return "notNull";
    return "null";
  }
  return null;
}

function humanizeFieldLabel(field: string | undefined | null): string {
  if (!field) return "field";
  return field.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}

function operatorPhrase(operator: string | undefined | null): string {
  if (!operator) return "matches";
  return OPERATOR_PHRASE[operator] ?? operator.replace(/_/g, " ");
}

function formatFieldCondition(
  field: string | undefined,
  operator: string | undefined | null,
  value: ComponentValue | undefined,
): string {
  const f = humanizeFieldLabel(field);
  if (!operator) return `${f} matches`;
  const nullOutcome = nullCheckOutcome(operator, value);
  if (nullOutcome === "null") return `${f} is null`;
  if (nullOutcome === "notNull") return `${f} is not null`;
  return `${f} ${operatorPhrase(operator)} ${valuePhrase(value)}`;
}

function fieldConditionParts(
  field: string | undefined,
  operator: string | undefined | null,
  value: ComponentValue | undefined,
  filterDef?: FilterDefinitionType | null,
): SummaryPart[] {
  const f = humanizeFieldLabel(field);
  if (!operator) return [text(f, "filter"), text(" matches", "operation")];
  const nullOutcome = nullCheckOutcome(operator, value);
  if (nullOutcome === "null")
    return [text(f, "filter"), text(" is null", "operation")];
  if (nullOutcome === "notNull")
    return [text(f, "filter"), text(" is not null", "operation")];
  return [
    text(f, "filter"),
    text(` ${operatorPhrase(operator)} `, "operation"),
    valuePart(value, filterDef),
  ];
}

/**
 * Build a ``SummaryPart`` for the RHS of a filter / rule, attaching
 * the live-reference resolver hooks when the LHS is a reference column
 * and the value is a constant identifier. Without ``filterDef`` (or
 * for non-reference filters) this collapses to ``text(valuePhrase, …)``
 * — the original behaviour.
 */
function valuePart(
  value: ComponentValue | undefined,
  filterDef?: FilterDefinitionType | null,
): SummaryPart {
  const tone =
    value && "type" in value && value.type === "variable"
      ? ("variable" as const)
      : ("value" as const);
  const phrase = valuePhrase(value);
  const isReferenceConstant =
    filterDef?.type === "reference" &&
    !!value &&
    "type" in value &&
    value.type === "constant" &&
    value.value !== "" &&
    value.value != null;
  if (!isReferenceConstant) return text(phrase, tone);
  let referenceLookup: ReferenceValueOptionsType<unknown> | undefined;
  try {
    referenceLookup =
      (filterDef?.getValueOptions?.(undefined) as
        | ReferenceValueOptionsType<unknown>
        | undefined) ?? undefined;
  } catch {
    referenceLookup = undefined;
  }
  return {
    ...text(phrase, tone),
    referenceLookup,
    referenceIdentifier: String((value as { value?: unknown }).value),
  };
}

function valuePhrase(value: ComponentValue | undefined): string {
  if (!value) return "?";
  if ("type" in value && value.type === "input" && "inputKey" in value) {
    return `input:${value.inputKey}`;
  }
  if ("type" in value && value.type === "variable") {
    if ("nodeId" in value) {
      const path = value.path || "";
      return path ? `\${${path}}` : "previous result";
    }
    return "previous result";
  }
  if (
    "value" in value &&
    value.value !== undefined &&
    value.value !== null &&
    value.value !== ""
  ) {
    const v = value.value;
    if (typeof v === "string") return `"${v}"`;
    return String(v);
  }
  return "?";
}

function joinList(parts: string[], joiner = "and"): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} ${joiner} ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, ${joiner} ${parts[parts.length - 1]}`;
}

function filterPhrase(filter: ComponentFilter): string {
  return formatFieldCondition(filter.field, filter.operator, filter.value);
}

function filterParts(
  filter: {
    field: string;
    operator: string;
    value: ComponentValue;
  },
  filterDefinitions?: Record<string, FilterDefinitionType>,
): SummaryPart[] {
  return fieldConditionParts(
    filter.field,
    filter.operator,
    filter.value,
    filterDefinitions?.[filter.field],
  );
}

function returnQuantifier(c: LookupComponent | ActionComponent): string {
  if (c.returnMode === "list") {
    if ("limit" in c && c.limit && c.limit > 0) return `up to ${c.limit}`;
    return "all";
  }
  return "orderBy" in c && c.orderBy ? "the first" : "one";
}

function summarizeLookupParts(
  c: LookupComponent,
  filterDefinitions?: Record<string, FilterDefinitionType>,
): SummaryPart[] {
  const noun = humanizeObjectType(c.objectType);
  const nounPlural = c.returnMode === "list" ? `${noun}s` : noun;
  const parts: SummaryPart[] = [
    text("Find "),
    text(returnQuantifier(c), "limit"),
    text(" "),
    text(nounPlural, "object"),
  ];

  if (c.filters && c.filters.length > 0) {
    parts.push(text(" where"));
    const filterJoined = joinParts(
      c.filters.map((f) => filterParts(f, filterDefinitions)),
      "and",
    );
    const [first, ...rest] = filterJoined;
    parts.push({
      ...first,
      text: ` ${first.text}`,
      breakBefore: true,
      indent: first.indent ?? 1,
    });
    parts.push(...rest);
    if (c.orderBy) {
      parts.push(text(", sorted by "));
      parts.push(text(c.orderBy, "sort"));
    }
  } else if (c.orderBy) {
    parts.push(text(", sorted by "));
    parts.push(text(c.orderBy, "sort"));
  }

  return parts;
}

function summarizeLookup(c: LookupComponent): string {
  return summaryText(summarizeLookupParts(c));
}

function summarizeActionParts(c: ActionComponent): SummaryPart[] {
  const noun = humanizeObjectType(c.objectType);
  const noPrompt = !c.prompt || c.prompt.trim() === "";
  if (noPrompt) {
    return [text("Generate a "), text(noun, "object")];
  }
  const prompt = c.prompt.length > 80 ? `${c.prompt.slice(0, 77)}…` : c.prompt;
  return [
    text("Generate a "),
    text(noun, "object"),
    text(" from "),
    text(`"${prompt}"`, "prompt", { breakBefore: true, indent: 1 }),
  ];
}

function summarizeAction(c: ActionComponent): string {
  return summaryText(summarizeActionParts(c));
}

function summarizeThreadParts(c: ThreadComponent): SummaryPart[] {
  if (!c.message || c.message.trim() === "")
    return [text("Send a thread message")];
  const msg = c.message.length > 80 ? `${c.message.slice(0, 77)}…` : c.message;
  return [text("Send: "), text(`"${msg}"`, "prompt")];
}

function summarizeThread(c: ThreadComponent): string {
  return summaryText(summarizeThreadParts(c));
}

export function summarizeStepComponentParts(
  component: StepComponent | undefined | null,
  filterDefinitions?: Record<string, FilterDefinitionType>,
): SummaryPart[] {
  if (!component) return [text("Not configured")];
  switch (component.type) {
    case "lookup":
      return summarizeLookupParts(component, filterDefinitions);
    case "action":
      return summarizeActionParts(component);
    case "thread":
      return summarizeThreadParts(component);
    default:
      return [text("Configure this step")];
  }
}

export function summarizeStepComponent(
  component: StepComponent | undefined | null,
): string {
  return summaryText(summarizeStepComponentParts(component));
}

const OPERATION_VERB: Record<string, string> = {
  ON_CREATE: "is created",
  ON_UPDATE: "is updated",
  ON_DELETE: "is deleted",
};

export function summarizeTrigger(trigger?: TriggerConfig | null): string {
  return summaryText(summarizeTriggerParts(trigger));
}

export function summarizeTriggerParts(
  trigger?: TriggerConfig | null,
): SummaryPart[] {
  if (!trigger || trigger.type === "manual") return [text("Started manually")];
  if (trigger.type === "event") {
    // ``event`` may be undefined transiently — the trigger type select can
    // flip to ``"event"`` a tick before the bridge writes the event sub-blob
    // back into the React Flow node data. Default each field individually so
    // the canvas card stays renderable instead of throwing.
    const event: Partial<EventTriggerConfig> = trigger.event ?? {};
    const noun = humanizeObjectType(event.objectType);
    const verb = OPERATION_VERB[event.operation] ?? "changes";
    const filterCount = Array.isArray(event.filters)
      ? event.filters.length
      : (event.filters?.children.length ?? 0);
    const filterPart =
      filterCount > 0
        ? `matching ${filterCount} condition${filterCount === 1 ? "" : "s"}`
        : "";
    // Per-field gate (ON_UPDATE only): summarise the picked fields so the
    // canvas card hints at the trigger scope without opening the panel.
    const watchFields =
      event.operation === "ON_UPDATE" ? (event.watchFields ?? []) : [];
    const watchPart =
      watchFields.length === 1
        ? `when ${humanizeFieldLabel(watchFields[0])} changes`
        : watchFields.length > 1
          ? `when ${watchFields.length} fields change`
          : "";
    const article = /^[aeiou]/.test(noun) ? "an" : "a";
    return [
      text(`When ${article} `),
      text(noun, "object"),
      text(` ${verb}`, "operation"),
      ...(watchPart ? [text(` · ${watchPart}`, "filter")] : []),
      ...(filterPart ? [text(` · ${filterPart}`, "filter")] : []),
    ];
  }
  if (trigger.type === "schedule") {
    const spec = resolveScheduleSpecForSummary(trigger.schedule);
    return [text(scheduleSummaryOrFallback(spec), "schedule")];
  }
  return [text("Trigger")];
}

export function summarizeSwitch(_rule?: unknown): string {
  return "Unsupported switch (legacy)";
}

export function summarizeSwitchParts(_rule?: unknown): SummaryPart[] {
  return [text("Unsupported switch (legacy)")];
}

const KIND_LABELS: Record<string, string> = {
  lookup: "Lookup",
  action: "Action",
  thread: "Thread",
};

export const SUGGESTION_TYPE_LABELS: Record<
  RuleDefinitionSuggestionTypeEnum,
  string
> = {
  [RuleDefinitionSuggestionTypeEnum.MatchDocument]: "Match document",
  [RuleDefinitionSuggestionTypeEnum.Categorize]: "Categorize",
  [RuleDefinitionSuggestionTypeEnum.Duplicate]: "Duplicate",
  [RuleDefinitionSuggestionTypeEnum.Split]: "Split",
  [RuleDefinitionSuggestionTypeEnum.Transfer]: "Transfer",
  [RuleDefinitionSuggestionTypeEnum.Pair]: "Pair",
};

export function actionKindLabel(
  component: StepComponent | undefined | null,
): string {
  if (!component) return "Action";
  return KIND_LABELS[component.type] ?? component.type;
}

// re-export so callers can render operator phrases consistently
export { operatorPhrase as nodeOperatorPhrase };

export type ServerSummaryPart = {
  text: string;
  tone?: SummaryTone | string | null;
  breakBefore?: boolean | null;
  indent?: number | null;
  referenceObjectType?: string | null;
  referenceIdentifier?: string | null;
};

export function mapServerSummaryParts(
  parts: ServerSummaryPart[] | null | undefined,
): SummaryPart[] {
  if (!parts?.length) return [];
  return parts.map((part) => ({
    text: part.text,
    tone: (part.tone ?? undefined) as SummaryPart["tone"],
    breakBefore: part.breakBefore ?? undefined,
    indent: part.indent ?? undefined,
    referenceObjectType: part.referenceObjectType ?? undefined,
    referenceIdentifier: part.referenceIdentifier ?? undefined,
  }));
}
